import React, { useState, useEffect, useMemo } from 'react';
import {
  Container, Typography, Box, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, IconButton, Tooltip, Chip, Avatar,
  Stack, LinearProgress, CircularProgress, TablePagination, Alert, ToggleButton, ToggleButtonGroup, Card, CardContent
} from '@mui/material';
import {
  Add as AddIcon, Refresh as RefreshIcon, PersonOff as PersonOffIcon, AssignmentInd as AssignedIcon,
  Group as GroupIcon, ViewList as ViewListIcon, WarningAmber as WarningIcon,
  AutoAwesome as SparkleIcon, People as PeopleIcon, FlightTakeoff as TripIcon
} from '@mui/icons-material';
import moment from 'moment';
import { getAllLeads, createLead, updateLead, deleteLead, getQuotationsByEmail } from '../../api/verifiedLeadsAPI';
import { checkDuplicateInquiries } from '../../api/inquiryAPI';
import { getAllUsers } from '../../api/userAPI';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setSelectedInquiry } from '../../reduxcomponents/slices/inquirySlice';

import { groupLeadsByCustomer, normalizePhone, normalizeEmail } from './utils';
import CustomerGroupRow from './CustomerGroupRow';
import FlatLeadRow from './FlatLeadRow';
import UnifyAssigneeDialog from './UnifyAssigneeDialog';
import MergeLeadsDialog from './MergeLeadsDialog';

const LeadTable = ({
  title,
  leads,
  icon,
  headerColor,
  emptyMsg,
  userRole,
  users,
  onEdit,
  onDelete,
  onCallHistory,
  onGenerateQuote,
  onQuickAssign,
  onViewQuotations,
  showStatusFilter,
  statusFilter,
  onStatusFilterChange,
  onChangeStatus,
  onOpenUnifyDialog,
  onOpenMergeDialog,
  onAddNewTripForGuest
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [viewMode, setViewMode] = useState('grouped'); // 'grouped' | 'flat'
  const [filterType, setFilterType] = useState('all'); // 'all' | 'multi' | 'single'

  // Cluster leads by shared customer contact
  const allGroups = useMemo(() => groupLeadsByCustomer(leads), [leads]);

  // Filter groups by multi/single inquiry if requested
  const filteredGroups = useMemo(() => {
    if (filterType === 'multi') return allGroups.filter(g => g.isMultiLead);
    if (filterType === 'single') return allGroups.filter(g => !g.isMultiLead);
    return allGroups;
  }, [allGroups, filterType]);

  // Create a fast lookup map for customer lead count for flat view
  const customerLeadCountMap = useMemo(() => {
    const map = new Map();
    for (const g of allGroups) {
      for (const l of g.leads) {
        map.set(l._id, {
          count: g.inquiriesCount,
          hasSplit: g.hasMultipleAssignees
        });
      }
    }
    return map;
  }, [allGroups]);

  // Reset page when dataset or filter changes
  useEffect(() => {
    setPage(0);
  }, [leads, viewMode, filterType]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const multiGroupCount = allGroups.filter(g => g.isMultiLead).length;
  const singleGroupCount = allGroups.length - multiGroupCount;

  // Pagination slicing
  const displayedItems = useMemo(() => {
    if (viewMode === 'grouped') {
      return filteredGroups.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    }
    return leads.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [viewMode, filteredGroups, leads, page, rowsPerPage]);

  const totalCount = viewMode === 'grouped' ? filteredGroups.length : leads.length;

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 4,
        overflow: 'hidden',
        mb: 4,
        border: '1px solid rgba(226, 232, 240, 0.8)',
        boxShadow: '0 10px 30px -10px rgba(0,0,0,0.08), 0 4px 6px -4px rgba(0,0,0,0.04)',
        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
        '&:hover': {
          boxShadow: '0 20px 40px -10px rgba(0,0,0,0.12), 0 8px 16px -8px rgba(0,0,0,0.06)'
        }
      }}
    >
      {/* ── Table header banner ── */}
      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          py: 2.5,
          background: headerColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(45deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0) 100%)',
            pointerEvents: 'none'
          }
        }}
      >
        {/* Left: Title & Counts */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, zIndex: 1 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 1.2,
              borderRadius: 2,
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.2)'
            }}
          >
            {icon}
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={800} color="#fff" sx={{ letterSpacing: '-0.02em', lineHeight: 1.2 }}>
              {title}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 500 }}>
              {allGroups.length} unique customers &nbsp;·&nbsp; {leads.length} total trip inquiries
              {multiGroupCount > 0 && ` · ${multiGroupCount} multi-inquiry`}
            </Typography>
          </Box>
        </Box>

        {/* Right: View Toggle, Multi Filter & Status Filter */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, zIndex: 1, flexWrap: 'wrap' }}>
          {/* View Mode Toggle */}
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(e, val) => val && setViewMode(val)}
            size="small"
            sx={{
              bgcolor: 'rgba(0, 0, 0, 0.2)',
              backdropFilter: 'blur(8px)',
              borderRadius: 2,
              p: 0.3,
              '& .MuiToggleButton-root': {
                color: 'rgba(255,255,255,0.8)',
                border: 'none',
                borderRadius: '6px !important',
                px: 1.5,
                py: 0.5,
                fontSize: '0.78rem',
                fontWeight: 600,
                textTransform: 'none',
                '&.Mui-selected': {
                  bgcolor: '#ffffff',
                  color: '#1e3a8a',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                  '&:hover': { bgcolor: '#ffffff' }
                },
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.1)',
                  color: '#ffffff'
                }
              }
            }}
          >
            <ToggleButton value="grouped">
              <GroupIcon fontSize="small" sx={{ mr: 0.5, fontSize: 16 }} />
              By Customer
            </ToggleButton>
            <ToggleButton value="flat">
              <ViewListIcon fontSize="small" sx={{ mr: 0.5, fontSize: 16 }} />
              All Leads
            </ToggleButton>
          </ToggleButtonGroup>

          {/* Customer Type Filter (Only in grouped mode) */}
          {viewMode === 'grouped' && (
            <TextField
              select
              size="small"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              InputProps={{
                sx: {
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.6)' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#fff' },
                  '& .MuiSvgIcon-root': { color: 'rgba(255,255,255,0.8)' }
                }
              }}
              sx={{
                bgcolor: 'rgba(0,0,0,0.15)',
                backdropFilter: 'blur(8px)',
                borderRadius: 2,
                minWidth: 155,
                '& .MuiSelect-select': { py: 0.7 }
              }}
            >
              <MenuItem value="all" sx={{ fontWeight: 600, fontSize: '0.825rem' }}>
                All Customers ({allGroups.length})
              </MenuItem>
              <MenuItem value="multi" sx={{ fontWeight: 600, fontSize: '0.825rem', color: '#4f46e5' }}>
                🔥 Multi-Trip Only ({multiGroupCount})
              </MenuItem>
              <MenuItem value="single" sx={{ fontWeight: 600, fontSize: '0.825rem' }}>
                Single Trip ({singleGroupCount})
              </MenuItem>
            </TextField>
          )}

          {/* Status Filter */}
          {showStatusFilter && (
            <TextField
              select
              size="small"
              value={statusFilter}
              onChange={(e) => onStatusFilterChange(e.target.value)}
              InputProps={{
                sx: {
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.6)' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#fff' },
                  '& .MuiSvgIcon-root': { color: 'rgba(255,255,255,0.8)' }
                }
              }}
              sx={{
                bgcolor: 'rgba(0,0,0,0.15)',
                backdropFilter: 'blur(8px)',
                borderRadius: 2,
                minWidth: 140,
                '& .MuiSelect-select': { py: 0.7 }
              }}
            >
              <MenuItem value="All" sx={{ fontWeight: 600, fontSize: '0.825rem' }}>All Statuses</MenuItem>
              <MenuItem value="New" sx={{ color: 'primary.main', fontWeight: 600, fontSize: '0.825rem' }}>New</MenuItem>
              <MenuItem value="In Progress" sx={{ color: 'warning.main', fontWeight: 600, fontSize: '0.825rem' }}>In Progress</MenuItem>
              <MenuItem value="Converted" sx={{ color: 'success.main', fontWeight: 600, fontSize: '0.825rem' }}>Converted</MenuItem>
              <MenuItem value="Lost" sx={{ color: 'error.main', fontWeight: 600, fontSize: '0.825rem' }}>Lost</MenuItem>
            </TextField>
          )}
        </Box>
      </Box>

      {/* ── Table Container ── */}
      <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
        <Table size="small" sx={{ width: '100%' }}>
          <TableHead sx={{ bgcolor: '#f8fafc' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, color: '#475569', minWidth: 200 }}>
                {viewMode === 'grouped' ? 'Customer Profile' : 'Guest'}
              </TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#475569', minWidth: 120 }}>Phone</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#475569', minWidth: 160 }}>
                {viewMode === 'grouped' ? 'Destinations Summary' : 'Destination / Pax'}
              </TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#475569', minWidth: 130 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#475569', minWidth: 160 }}>Assigned To</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#475569', minWidth: 130 }}>
                {viewMode === 'grouped' ? 'Latest Activity' : 'Date Added'}
              </TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#475569', width: 95 }} align="center">Quotes</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#475569', width: 120 }} align="center">Actions</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {totalCount === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 5, color: '#94a3b8' }}>
                  <Typography variant="body2">{emptyMsg}</Typography>
                </TableCell>
              </TableRow>
            ) : viewMode === 'grouped' ? (
              displayedItems.map(group => (
                <CustomerGroupRow
                  key={group.customerKey}
                  group={group}
                  userRole={userRole}
                  users={users}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onCallHistory={onCallHistory}
                  onGenerateQuote={onGenerateQuote}
                  onQuickAssign={onQuickAssign}
                  onViewQuotations={onViewQuotations}
                  onChangeStatus={onChangeStatus}
                  onOpenUnifyDialog={onOpenUnifyDialog}
                  onOpenMergeDialog={onOpenMergeDialog}
                  onAddNewTripForGuest={onAddNewTripForGuest}
                />
              ))
            ) : (
              displayedItems.map(lead => {
                const info = customerLeadCountMap.get(lead._id) || { count: 1, hasSplit: false };
                return (
                  <FlatLeadRow
                    key={lead._id}
                    lead={lead}
                    customerLeadCount={info.count}
                    hasSplitAssignee={info.hasSplit}
                    userRole={userRole}
                    users={users}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    onCallHistory={onCallHistory}
                    onGenerateQuote={onGenerateQuote}
                    onQuickAssign={onQuickAssign}
                    onViewQuotations={onViewQuotations}
                    onChangeStatus={onChangeStatus}
                    onSwitchToGrouped={() => setViewMode('grouped')}
                  />
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ── Pagination ── */}
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50, 100]}
        component="div"
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelDisplayedRows={({ from, to, count }) =>
          viewMode === 'grouped'
            ? `${from}–${to} of ${count} customers`
            : `${from}–${to} of ${count} leads`
        }
      />
    </Paper>
  );
};

const VerifiedLeads = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const userRole = useSelector((state) => state.tokens?.user?.permission);
  const userId = useSelector((state) => state.tokens?.user?._id || state.tokens?.user?.id);

  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [assignedUserFilter, setAssignedUserFilter] = useState('all');
  const [statusFilterAll, setStatusFilterAll] = useState('All');
  const [statusFilterAssigned, setStatusFilterAssigned] = useState('All');

  // Dialogs
  const [openAddEdit, setOpenAddEdit] = useState(false);
  const [openCallHistory, setOpenCallHistory] = useState(false);
  const [openQuotationsModal, setOpenQuotationsModal] = useState(false);
  const [quotationsList, setQuotationsList] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [currentLeadId, setCurrentLeadId] = useState(null);
  const [phoneHistoryResult, setPhoneHistoryResult] = useState(null);
  const [checkingPhoneHistory, setCheckingPhoneHistory] = useState(false);
  const [formData, setFormData] = useState({
    guest_name: '', guest_email: '', guest_phone: '', guest_address: '',
    destination: '', pax: '', kids_above_5: '', arrival_date: '', lead_source: 'WhatsApp', status: 'New', assigned_to: [], remarks: ''
  });
  const [selectedLead, setSelectedLead] = useState(null);
  const [newCallNote, setNewCallNote] = useState('');
  const [newCallTime, setNewCallTime] = useState('');
  const [newCallDuration, setNewCallDuration] = useState('');

  // Unify Assignee & Merge Inquiries Dialogs
  const [unifyDialogOpen, setUnifyDialogOpen] = useState(false);
  const [unifyTargetGroup, setUnifyTargetGroup] = useState(null);
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [mergeTargetGroup, setMergeTargetGroup] = useState(null);

  useEffect(() => {
    fetchLeads();
    fetchUsers();
  }, []);

  // Phone history check strictly by phone number
  useEffect(() => {
    if (!openAddEdit) {
      setPhoneHistoryResult(null);
      setCheckingPhoneHistory(false);
      return;
    }
    const cleanDigits = (formData.guest_phone || '').replace(/\D/g, '');
    if (cleanDigits.length < 10) {
      setPhoneHistoryResult(null);
      return;
    }
    const timer = setTimeout(async () => {
      setCheckingPhoneHistory(true);
      try {
        const res = await checkDuplicateInquiries(
          formData.guest_email || '',
          formData.guest_phone,
          currentLeadId,
          formData.destination || ''
        );
        if (res.success) {
          setPhoneHistoryResult(res);
        }
      } catch (e) {
        console.error('Phone history check error:', e);
      } finally {
        setCheckingPhoneHistory(false);
      }
    }, 450);

    return () => clearTimeout(timer);
  }, [openAddEdit, formData.guest_phone, formData.destination, currentLeadId, formData.guest_email]);

  useEffect(() => {
    if (location.state?.autoOpenAdd) {
      setIsEditing(false);
      setCurrentLeadId(null);
      setPhoneHistoryResult(null);
      setFormData({
        guest_name: '',
        guest_email: '',
        guest_phone: location.state.phone || '',
        guest_address: '',
        destination: '',
        pax: '',
        kids_above_5: '',
        arrival_date: '',
        lead_source: 'WhatsApp',
        status: 'New',
        assigned_to: userId ? [userId] : [],
        remarks: ''
      });
      setOpenAddEdit(true);

      // Clear state to prevent re-opening on refresh
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, userId, navigate, location.pathname]);

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const res = await getAllLeads();
      if (res.data?.success) {
        setLeads(res.data.leads || []);
      }
    } catch (err) {
      console.error('Error fetching leads:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await getAllUsers();
      if (res.data) setUsers(res.data.filter(u => u.isActive !== false));
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  const handleOpenAdd = () => {
    setIsEditing(false);
    setCurrentLeadId(null);
    setPhoneHistoryResult(null);
    setFormData({
      guest_name: '',
      guest_email: '',
      guest_phone: '',
      guest_address: '',
      destination: '',
      pax: '',
      kids_above_5: '',
      arrival_date: '',
      lead_source: 'WhatsApp',
      status: 'New',
      assigned_to: userId ? [userId] : [],
      remarks: ''
    });
    setOpenAddEdit(true);
  };

  // Pre-fill modal for an existing customer
  const handleAddNewTripForGuest = (customerGroup) => {
    setIsEditing(false);
    setCurrentLeadId(null);
    setPhoneHistoryResult(null);
    const existingAssignees = customerGroup.assignedUsers?.map(u => String(u._id || u.id)) || [];
    setFormData({
      guest_name: customerGroup.guest_name || '',
      guest_email: customerGroup.guest_email === '—' ? '' : customerGroup.guest_email,
      guest_phone: customerGroup.guest_phone === '—' ? '' : customerGroup.guest_phone,
      guest_address: customerGroup.guest_address || '',
      destination: '',
      pax: '',
      kids_above_5: '',
      arrival_date: '',
      lead_source: customerGroup.leads?.[0]?.lead_source || 'Offline',
      status: 'New',
      assigned_to: existingAssignees.length > 0 ? existingAssignees : (userId ? [userId] : []),
      remarks: `Additional trip inquiry for ${customerGroup.guest_name}`
    });
    setOpenAddEdit(true);
  };

  const handleOpenEdit = (lead) => {
    setIsEditing(true);
    setCurrentLeadId(lead._id);
    setPhoneHistoryResult(null);
    setFormData({
      guest_name: lead.guest_name || '',
      guest_email: lead.guest_email || '',
      guest_phone: lead.guest_phone || '',
      guest_address: lead.guest_address || '',
      destination: lead.destination || '',
      pax: lead.pax || '',
      kids_above_5: lead.kids_above_5 ?? '',
      arrival_date: lead.arrival_date ? moment(lead.arrival_date).format('YYYY-MM-DD') : '',
      lead_source: lead.lead_source || 'Direct',
      status: lead.status || 'New',
      assigned_to: Array.isArray(lead.assigned_to) ? lead.assigned_to.map(u => u._id || u.id || u) : [],
      remarks: lead.remarks || ''
    });
    setOpenAddEdit(true);
  };

  const submitAddEdit = async (andGenerateQuote = false) => {
    try {
      const payload = { ...formData };
      if (!payload.assigned_to || payload.assigned_to.length === 0) delete payload.assigned_to;
      if (!payload.arrival_date) delete payload.arrival_date;
      if (!payload.pax) delete payload.pax;
      if (payload.kids_above_5 === '' || payload.kids_above_5 === undefined) delete payload.kids_above_5;
      let savedLead = null;
      if (isEditing) {
        const res = await updateLead(currentLeadId, payload);
        savedLead = res.data?.lead || { ...payload, _id: currentLeadId };
      } else {
        const res = await createLead(payload);
        savedLead = res.data?.lead || payload;
      }
      setOpenAddEdit(false);
      fetchLeads();
      if (andGenerateQuote && savedLead) {
        dispatch(setSelectedInquiry(savedLead));
        navigate('/createItinerary');
      }
    } catch (err) {
      console.error('Submit Error:', err);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this verified lead inquiry?')) {
      try {
        await deleteLead(id);
        fetchLeads();
      } catch (err) {
        console.error('Delete Error:', err);
      }
    }
  };

  const handleOpenCallHistory = (lead) => {
    setSelectedLead(lead);
    setNewCallNote('');
    setNewCallTime(moment().format('YYYY-MM-DDTHH:mm'));
    setNewCallDuration('');
    setOpenCallHistory(true);
  };

  const handleAddCallNote = async () => {
    if (!newCallNote.trim()) return;
    try {
      await updateLead(selectedLead._id, {
        new_call_log: {
          note: newCallNote,
          call_time: newCallTime ? new Date(newCallTime) : new Date(),
          duration: newCallDuration
        }
      });
      setNewCallNote('');
      setNewCallTime('');
      setNewCallDuration('');
      fetchLeads();
      setOpenCallHistory(false);
    } catch (err) {
      console.error('Add call note error:', err);
    }
  };

  const handleGenerateQuote = (lead) => {
    dispatch(setSelectedInquiry(lead));
    navigate('/createItinerary');
  };

  const handleViewQuotations = async (lead) => {
    setSelectedLead(lead);
    setQuotationsList([]);
    setOpenQuotationsModal(true);
    try {
      const res = await getQuotationsByEmail(lead._id);
      if (res.data?.success) {
        setQuotationsList(res.data.quotations || []);
      }
    } catch (err) {
      console.error('Fetch Quotations Error:', err);
    }
  };

  const handleEditQuotation = (quotation) => {
    dispatch(setSelectedInquiry(quotation));
    navigate('/createItinerary');
  };

  const handleQuickAssign = async (leadId, newAssigneeId) => {
    try {
      await updateLead(leadId, { assigned_to: newAssigneeId });
      fetchLeads();
    } catch (err) {
      console.error('Quick assign error:', err);
    }
  };

  const handleChangeStatus = async (leadId, newStatus) => {
    try {
      await updateLead(leadId, { status: newStatus });
      fetchLeads();
    } catch (err) {
      console.error('Status change error:', err);
    }
  };

  // Reassign all leads of a customer to one sales executive
  const handleUnifyAssignee = async (customerGroup, userIds) => {
    for (const lead of customerGroup.leads) {
      await updateLead(lead._id, { assigned_to: userIds });
    }
    fetchLeads();
  };

  // Merge duplicate leads: transfer remarks & call history, then delete duplicate lead
  const handleMergeLeads = async (primaryLead, secondaryLead) => {
    const combinedRemarks = [
      primaryLead.remarks || '',
      secondaryLead.remarks ? `[Merged duplicate trip notes]: ${secondaryLead.remarks}` : ''
    ].filter(Boolean).join('\n\n');

    await updateLead(primaryLead._id, {
      remarks: combinedRemarks
    });

    // If secondary lead had calls, transfer each call note
    if (Array.isArray(secondaryLead.call_history)) {
      for (const call of secondaryLead.call_history) {
        await updateLead(primaryLead._id, {
          new_call_log: {
            note: `[Transferred Call]: ${call.note || ''}`,
            call_time: call.call_time || new Date(),
            duration: call.duration || ''
          }
        });
      }
    }

    // Delete redundant duplicate lead
    await deleteLead(secondaryLead._id);
    fetchLeads();
  };

  const salesExecs = useMemo(
    () => users.filter(u => ['Sales Executive', 'Sales Team Lead', 'Sales Manager'].includes(u.permission)),
    [users]
  );

  const isAdminView = ['Admin', 'Admin-a', 'Sales Manager', 'Sales Team Lead'].includes(userRole);

  // Group all leads globally to compute top metrics and check for duplicates
  const globalCustomerGroups = useMemo(() => groupLeadsByCustomer(leads), [leads]);
  const globalMultiGroupCount = globalCustomerGroups.filter(g => g.isMultiLead).length;
  const globalSplitAssigneeCount = globalCustomerGroups.filter(g => g.hasMultipleAssignees).length;

  // Real-time detection in "Add Lead" modal: check if contact already exists
  const detectedExistingCustomer = useMemo(() => {
    if (isEditing) return null;
    const phone = normalizePhone(formData.guest_phone);
    const email = normalizeEmail(formData.guest_email);
    if (!phone && !email) return null;

    return globalCustomerGroups.find(g => {
      const gPhone = normalizePhone(g.guest_phone);
      const gEmail = normalizeEmail(g.guest_email);
      return (phone && gPhone === phone) || (email && gEmail === email);
    });
  }, [formData.guest_phone, formData.guest_email, isEditing, globalCustomerGroups]);

  // Table 1: All leads — search + status filter applied
  const allLeadsFiltered = useMemo(() => leads.filter(lead => {
    if (statusFilterAll !== 'All' && lead.status !== statusFilterAll) return false;
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      lead.guest_name?.toLowerCase().includes(q) ||
      lead.guest_email?.toLowerCase().includes(q) ||
      lead.guest_phone?.toLowerCase().includes(q) ||
      lead.destination?.toLowerCase().includes(q)
    );
  }), [leads, searchQuery, statusFilterAll]);

  // Table 2: Assigned leads — search + chip filter + status filter applied
  const assignedLeads = useMemo(() => leads.filter(lead => {
    // Must be assigned
    if (!lead.assigned_to || (Array.isArray(lead.assigned_to) && lead.assigned_to.length === 0)) return false;
    if (statusFilterAssigned !== 'All' && lead.status !== statusFilterAssigned) return false;

    // Search filter
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      if (!(
        lead.guest_name?.toLowerCase().includes(q) ||
        lead.guest_email?.toLowerCase().includes(q) ||
        lead.guest_phone?.toLowerCase().includes(q) ||
        lead.destination?.toLowerCase().includes(q)
      )) return false;
    }

    // Chip filter: specific Sales Executive
    if (assignedUserFilter !== 'all') {
      const assignees = Array.isArray(lead.assigned_to) ? lead.assigned_to.map(u => String(u._id || u.id || u)) : [];
      return assignees.includes(String(assignedUserFilter));
    }
    return true;
  }), [leads, searchQuery, assignedUserFilter, statusFilterAssigned]);

  const tableHandlers = {
    userRole,
    users,
    onEdit: handleOpenEdit,
    onDelete: handleDelete,
    onCallHistory: handleOpenCallHistory,
    onGenerateQuote: handleGenerateQuote,
    onQuickAssign: handleQuickAssign,
    onViewQuotations: handleViewQuotations,
    onChangeStatus: handleChangeStatus,
    onOpenUnifyDialog: (grp) => {
      setUnifyTargetGroup(grp);
      setUnifyDialogOpen(true);
    },
    onOpenMergeDialog: (grp) => {
      setMergeTargetGroup(grp);
      setMergeDialogOpen(true);
    },
    onAddNewTripForGuest: handleAddNewTripForGuest
  };

  return (
    <Container maxWidth={false} sx={{ mt: 2, mb: 4, px: { xs: 1.5, sm: 2, md: 3 }, width: '100%' }}>
      {/* ── Page Header ── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={800} sx={{ letterSpacing: -0.5, color: '#0f172a' }}>
            Verified Leads
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Consolidated customer view &nbsp;·&nbsp; {globalCustomerGroups.length} unique customers &nbsp;·&nbsp; {leads.length} total inquiries
          </Typography>
        </Box>

        <Stack direction="row" spacing={1.5} alignItems="center">
          <TextField
            size="small"
            placeholder="Search name, email, phone, trip..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ bgcolor: '#fff', width: { xs: 200, sm: 280 } }}
          />
          <Tooltip title="Refresh Leads">
            <IconButton onClick={fetchLeads} sx={{ bgcolor: '#fff', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenAdd}
            sx={{
              whiteSpace: 'nowrap',
              fontWeight: 700,
              background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
              boxShadow: '0 4px 12px rgba(37, 99, 235, 0.25)',
              px: 2.5
            }}
          >
            Add Lead
          </Button>
        </Stack>
      </Box>

      {/* ── KPI Metrics Bar ── */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
        <Card variant="outlined" sx={{ borderRadius: 3, bgcolor: '#ffffff' }}>
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase' }}>
                Total Inquiries
              </Typography>
              <TripIcon sx={{ fontSize: 18, color: '#3b82f6' }} />
            </Box>
            <Typography variant="h5" fontWeight={800} sx={{ mt: 0.5, color: '#1e293b' }}>
              {leads.length}
            </Typography>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ borderRadius: 3, bgcolor: '#ffffff' }}>
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase' }}>
                Unique Customers
              </Typography>
              <PeopleIcon sx={{ fontSize: 18, color: '#10b981' }} />
            </Box>
            <Typography variant="h5" fontWeight={800} sx={{ mt: 0.5, color: '#1e293b' }}>
              {globalCustomerGroups.length}
            </Typography>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ borderRadius: 3, bgcolor: globalMultiGroupCount > 0 ? '#faf5ff' : '#ffffff', borderColor: globalMultiGroupCount > 0 ? '#e9d5ff' : '#e2e8f0' }}>
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="caption" color={globalMultiGroupCount > 0 ? '#7e22ce' : 'text.secondary'} fontWeight={700} sx={{ textTransform: 'uppercase' }}>
                Multi-Trip Clients
              </Typography>
              <SparkleIcon sx={{ fontSize: 18, color: '#a855f7' }} />
            </Box>
            <Typography variant="h5" fontWeight={800} sx={{ mt: 0.5, color: globalMultiGroupCount > 0 ? '#6b21a8' : '#1e293b' }}>
              {globalMultiGroupCount}
            </Typography>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ borderRadius: 3, bgcolor: globalSplitAssigneeCount > 0 ? '#fffbeb' : '#ffffff', borderColor: globalSplitAssigneeCount > 0 ? '#fde68a' : '#e2e8f0' }}>
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="caption" color={globalSplitAssigneeCount > 0 ? '#b45309' : 'text.secondary'} fontWeight={700} sx={{ textTransform: 'uppercase' }}>
                Split Assigned
              </Typography>
              <WarningIcon sx={{ fontSize: 18, color: globalSplitAssigneeCount > 0 ? '#f59e0b' : '#94a3b8' }} />
            </Box>
            <Typography variant="h5" fontWeight={800} sx={{ mt: 0.5, color: globalSplitAssigneeCount > 0 ? '#b45309' : '#1e293b' }}>
              {globalSplitAssigneeCount}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

      {/* ── Filter chips (Admin/Sales Manager only) ── */}
      {isAdminView && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3, p: 1.5, bgcolor: '#f8fafc', borderRadius: 2.5, border: '1px solid #e2e8f0' }}>
          <Typography variant="caption" fontWeight={700} color="#64748b" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, mr: 1, alignSelf: 'center' }}>
            Filter Rep:
          </Typography>
          <Chip
            label={`All Leads (${leads.length})`}
            onClick={() => setAssignedUserFilter('all')}
            variant={assignedUserFilter === 'all' ? 'filled' : 'outlined'}
            color={assignedUserFilter === 'all' ? 'primary' : 'default'}
            sx={{ fontWeight: 600, cursor: 'pointer' }}
          />
          {salesExecs.map(u => {
            const count = leads.filter(l => Array.isArray(l.assigned_to) && l.assigned_to.some(a => String(a._id || a.id || a) === String(u.id))).length;
            const active = assignedUserFilter === u.id;
            return (
              <Chip
                key={u.id}
                avatar={<Avatar sx={{ bgcolor: active ? '#fff' : '#e2e8f0', color: active ? 'primary.main' : '#475569', fontWeight: 700 }}>{u.fullName?.charAt(0)}</Avatar>}
                label={`${u.fullName} (${count})`}
                onClick={() => setAssignedUserFilter(active ? 'all' : u.id)}
                variant={active ? 'filled' : 'outlined'}
                color={active ? 'primary' : 'default'}
                sx={{ fontWeight: 600, cursor: 'pointer' }}
              />
            );
          })}
        </Box>
      )}

      {/* ── SPLIT TABLES (Admin / Sales Manager view) ── */}
      {isAdminView ? (
        <>
          {/* Table 1: ALL leads */}
          <LeadTable
            title="All Verified Leads"
            leads={allLeadsFiltered}
            icon={<AssignedIcon sx={{ color: '#fff' }} />}
            headerColor="linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)"
            emptyMsg="No leads found."
            showStatusFilter={true}
            statusFilter={statusFilterAll}
            onStatusFilterChange={setStatusFilterAll}
            {...tableHandlers}
          />

          {/* Table 2: ASSIGNED leads (filtered by chip) */}
          <LeadTable
            title={assignedUserFilter !== 'all'
              ? `Assigned to: ${salesExecs.find(u => u.id === assignedUserFilter)?.fullName || 'Selected User'}`
              : 'Assigned Leads'}
            leads={assignedLeads}
            icon={<PersonOffIcon sx={{ color: '#fff' }} />}
            headerColor="linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)"
            emptyMsg={assignedUserFilter !== 'all' ? 'No leads assigned to this user.' : 'No assigned leads yet.'}
            showStatusFilter={true}
            statusFilter={statusFilterAssigned}
            onStatusFilterChange={setStatusFilterAssigned}
            {...tableHandlers}
          />
        </>
      ) : (
        /* ── Single table for Sales Executive ── */
        <LeadTable
          title="My Verified Leads"
          leads={allLeadsFiltered}
          icon={<AssignedIcon sx={{ color: '#fff' }} />}
          headerColor="linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)"
          emptyMsg="No leads assigned to you yet."
          showStatusFilter={true}
          statusFilter={statusFilterAll}
          onStatusFilterChange={setStatusFilterAll}
          {...tableHandlers}
        />
      )}

      {/* ── Add / Edit Dialog with Instant Duplicate Detection ── */}
      <Dialog open={openAddEdit} onClose={() => setOpenAddEdit(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>
          {isEditing ? 'Edit Trip Inquiry' : 'Add New Verified Lead'}
        </DialogTitle>
        <DialogContent dividers>
          {/* Instant Duplicate Alert Banner */}
          {detectedExistingCustomer && (
            <Alert
              severity="info"
              sx={{ mb: 2, borderRadius: 2 }}
              action={
                <Button
                  size="small"
                  variant="outlined"
                  color="inherit"
                  onClick={() => {
                    setFormData(prev => ({
                      ...prev,
                      guest_name: detectedExistingCustomer.guest_name || prev.guest_name,
                      guest_email: (detectedExistingCustomer.guest_email !== '—' ? detectedExistingCustomer.guest_email : '') || prev.guest_email,
                      guest_phone: (detectedExistingCustomer.guest_phone !== '—' ? detectedExistingCustomer.guest_phone : '') || prev.guest_phone,
                      guest_address: detectedExistingCustomer.guest_address || prev.guest_address,
                      assigned_to: detectedExistingCustomer.assignedUsers.map(u => u._id || u.id)
                    }));
                  }}
                  sx={{ fontWeight: 700, fontSize: '0.72rem', whiteSpace: 'nowrap' }}
                >
                  Auto-fill Info
                </Button>
              }
            >
              Existing verified customer: <strong>{detectedExistingCustomer.guest_name}</strong> ({detectedExistingCustomer.inquiriesCount} existing trips).
            </Alert>
          )}

          {/* Phone History & Loyalty Detection Banner (Across All Collections) */}
          {checkingPhoneHistory && (
            <Box sx={{ p: 1.5, mb: 2, display: 'flex', alignItems: 'center', gap: 1.5, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
              <CircularProgress size={18} />
              <Typography variant="caption" color="text.secondary">Checking phone history across all CRM records...</Typography>
            </Box>
          )}

          {!checkingPhoneHistory && phoneHistoryResult && (
            <Box sx={{ mb: 2 }}>
              {phoneHistoryResult.isLoyalGuest && (
                <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#f0fdf4', border: '1px solid #86efac', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip label="⭐ Loyal Guest" color="success" size="small" sx={{ fontWeight: 800 }} />
                  <Typography variant="body2" sx={{ color: '#166534', fontWeight: 600 }}>
                    Recognized Loyal Guest ({phoneHistoryResult.confirmedTripsCount || 1} confirmed trip bookings).
                  </Typography>
                </Box>
              )}
              {!phoneHistoryResult.isLoyalGuest && phoneHistoryResult.isDuplicate && (
                <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#fff7ed', border: '1px solid #fdba74', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip label="⚠️ Duplicate Inquiry" color="warning" size="small" sx={{ fontWeight: 800 }} />
                  <Typography variant="body2" sx={{ color: '#9a3412', fontWeight: 600 }}>
                    Client previously inquired for the same destination ({formData.destination || 'same destination'}).
                  </Typography>
                </Box>
              )}
              {!phoneHistoryResult.isLoyalGuest && phoneHistoryResult.isMultiple && (
                <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#eff6ff', border: '1px solid #93c5fd', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip label="🔁 Multiple Inquiries" color="info" size="small" sx={{ fontWeight: 800 }} />
                  <Typography variant="body2" sx={{ color: '#1e40af', fontWeight: 600 }}>
                    Client has {phoneHistoryResult.totalPreviousTrips} previous trip inquiries across other destinations.
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          <TextField
            margin="dense"
            label="Guest Phone"
            name="guest_phone"
            fullWidth
            value={formData.guest_phone}
            onChange={e => setFormData({ ...formData, [e.target.name]: e.target.value })}
            required
            helperText="Matches existing customer records strictly by phone number"
          />

          <TextField
            margin="dense"
            label="Guest Name"
            name="guest_name"
            fullWidth
            value={formData.guest_name}
            onChange={e => setFormData({ ...formData, [e.target.name]: e.target.value })}
            required
          />

          <TextField
            margin="dense"
            label="Guest Email"
            name="guest_email"
            fullWidth
            value={formData.guest_email}
            onChange={e => setFormData({ ...formData, [e.target.name]: e.target.value })}
          />

          <TextField
            margin="dense"
            label="Guest Address"
            name="guest_address"
            fullWidth
            value={formData.guest_address}
            onChange={e => setFormData({ ...formData, [e.target.name]: e.target.value })}
          />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              margin="dense"
              label="Trip Destination"
              name="destination"
              fullWidth
              value={formData.destination}
              onChange={e => setFormData({ ...formData, [e.target.name]: e.target.value })}
            />
            <TextField
              margin="dense"
              label="Pax"
              name="pax"
              type="number"
              fullWidth
              value={formData.pax}
              onChange={e => setFormData({ ...formData, [e.target.name]: e.target.value })}
              inputProps={{ min: 1 }}
            />
            <TextField
              margin="dense"
              label="Children (Age 5 to 11 yr)"
              name="kids_above_5"
              type="number"
              fullWidth
              value={formData.kids_above_5}
              onChange={e => setFormData({ ...formData, [e.target.name]: e.target.value })}
              inputProps={{ min: 0 }}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              select
              margin="dense"
              label="Lead Source"
              name="lead_source"
              fullWidth
              value={formData.lead_source || 'WhatsApp'}
              onChange={e => setFormData({ ...formData, [e.target.name]: e.target.value })}
            >
              <MenuItem value="WhatsApp">💬 WhatsApp</MenuItem>
              <MenuItem value="Instagram">📸 Instagram</MenuItem>
              <MenuItem value="Offline">🏢 Offline / Call</MenuItem>
              <MenuItem value="Walk-in">🚶 Walk-in</MenuItem>
              <MenuItem value="Facebook">📘 Facebook</MenuItem>
              <MenuItem value="Website">🌐 Website</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </TextField>

            <TextField
              select
              margin="dense"
              label="Status"
              name="status"
              fullWidth
              value={formData.status}
              onChange={e => setFormData({ ...formData, [e.target.name]: e.target.value })}
            >
              {['New', 'In Progress', 'Converted', 'Lost'].map(s => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </TextField>
          </Box>

          <TextField
            margin="dense"
            label="Arrival Date"
            name="arrival_date"
            type="date"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={formData.arrival_date}
            onChange={e => setFormData({ ...formData, [e.target.name]: e.target.value })}
          />

          {isAdminView && userRole !== 'Sales Team Lead' && (
            <TextField
              select
              margin="dense"
              label="Assign To"
              name="assigned_to"
              fullWidth
              SelectProps={{ multiple: true }}
              value={formData.assigned_to || []}
              onChange={e => setFormData({ ...formData, [e.target.name]: e.target.value })}
            >
              {salesExecs.map(u => (
                <MenuItem key={u.id} value={u.id}>{u.fullName}</MenuItem>
              ))}
            </TextField>
          )}

          <TextField
            margin="dense"
            label="Remarks"
            name="remarks"
            multiline
            rows={3}
            fullWidth
            value={formData.remarks}
            onChange={e => setFormData({ ...formData, [e.target.name]: e.target.value })}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button onClick={() => setOpenAddEdit(false)}>Cancel</Button>
          <Button
            onClick={() => submitAddEdit(false)}
            variant="outlined"
            disabled={!formData.guest_name || !formData.guest_phone}
            sx={{ fontWeight: 600 }}
          >
            {isEditing ? 'Save Changes' : 'Create Lead Only'}
          </Button>
          <Button
            onClick={() => submitAddEdit(true)}
            variant="contained"
            disabled={!formData.guest_name || !formData.guest_phone}
            sx={{ fontWeight: 700 }}
          >
            {isEditing ? 'Save & Open Quotation' : 'Save & Generate Quote'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Unify Assignee Dialog ── */}
      <UnifyAssigneeDialog
        open={unifyDialogOpen}
        onClose={() => setUnifyDialogOpen(false)}
        customerGroup={unifyTargetGroup}
        salesExecs={salesExecs}
        onUnify={handleUnifyAssignee}
      />

      {/* ── Merge Duplicate Leads Dialog ── */}
      <MergeLeadsDialog
        open={mergeDialogOpen}
        onClose={() => setMergeDialogOpen(false)}
        customerGroup={mergeTargetGroup}
        onMerge={handleMergeLeads}
      />

      {/* ── Call History Dialog ── */}
      <Dialog open={openCallHistory} onClose={() => setOpenCallHistory(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Call Logs — {selectedLead?.guest_name}</DialogTitle>
        <DialogContent dividers>
          {selectedLead?.call_history?.length > 0 ? (
            <Box sx={{ mb: 3 }}>
              {selectedLead.call_history.map((call, i) => (
                <Box key={i} sx={{ mb: 2, p: 2, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                  <Typography variant="body2" color="text.secondary" fontWeight="bold">
                    {moment(call.call_time).format('DD MMM YYYY, hh:mm A')} {call.duration && `— Duration: ${call.duration}`}
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5 }}>{call.note}</Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography sx={{ mb: 3 }} color="text.secondary">No call history recorded yet for this trip.</Typography>
          )}

          <Typography variant="subtitle1" fontWeight="bold">Log a new call</Typography>
          <Box sx={{ display: 'flex', gap: 2, mb: 1, mt: 2 }}>
            <TextField
              margin="dense"
              label="Call Time"
              type="datetime-local"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={newCallTime}
              onChange={e => setNewCallTime(e.target.value)}
            />
            <TextField
              margin="dense"
              label="Duration (e.g. 5 mins)"
              fullWidth
              value={newCallDuration}
              onChange={e => setNewCallDuration(e.target.value)}
              placeholder="e.g. 5 mins"
            />
          </Box>
          <TextField
            margin="dense"
            label="Call Notes"
            multiline
            rows={2}
            fullWidth
            value={newCallNote}
            onChange={e => setNewCallNote(e.target.value)}
            placeholder="E.g., Called customer, confirmed dates for Sikkim trip."
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setOpenCallHistory(false)}>Close</Button>
          <Button onClick={handleAddCallNote} variant="contained" disabled={!newCallNote.trim()}>
            Save Call Log
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Quotations List Dialog ── */}
      <Dialog open={openQuotationsModal} onClose={() => setOpenQuotationsModal(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Quotations — {selectedLead?.guest_name}</DialogTitle>
        <DialogContent dividers>
          {quotationsList.length > 0 ? (
            <TableContainer>
              <Table size="small">
                <TableHead sx={{ bgcolor: '#f8fafc' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Quote No</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Destination</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Travel Date</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Cost</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Stage</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {quotationsList.map((quote) => (
                    <TableRow key={quote._id}>
                      <TableCell>{quote.quotation_no || 'N/A'}</TableCell>
                      <TableCell>{quote.destination || 'N/A'}</TableCell>
                      <TableCell>{quote.travel_date ? moment(quote.travel_date).format('DD MMM YYYY') : 'N/A'}</TableCell>
                      <TableCell>₹{quote.cost?.toLocaleString()}</TableCell>
                      <TableCell>
                        <Chip label={quote.lead_stage} size="small" color={quote.lead_stage === 'Confirm' ? 'success' : 'primary'} />
                      </TableCell>
                      <TableCell align="center">
                        <Button size="small" variant="outlined" onClick={() => handleEditQuotation(quote)}>
                          Edit / View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography sx={{ mb: 3 }} color="text.secondary">No quotations found for this trip.</Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setOpenQuotationsModal(false)}>Close</Button>
        </DialogActions>
      </Dialog>

    </Container>
  );
};

export default VerifiedLeads;
